import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminShell from '../components/admin/AdminShell';
import {
  assignAdminAssistedDispatchDriver,
  fetchAdminAssistedDispatchCandidates,
  fetchAdminAssistedDispatchQueue,
  sendAdminWhatsAppMessage,
} from '../services/api/admin';
import type { AdminAssistedDispatchCandidate, AdminAssistedDispatchQueueItem } from '../types/admin';
import { formatCurrency, formatDateTime } from '../utils/companyOwner';
import '../styles/register.css';
import '../styles/company-owner.css';
import '../styles/admin.css';

type CandidateState = {
  loading: boolean;
  error: string;
  items: AdminAssistedDispatchCandidate[];
};

function formatCompactNumber(value: number | null | undefined): string {
  return new Intl.NumberFormat('en-ZA').format(Number(value ?? 0));
}

function defaultWhatsAppAdminMessage(item: AdminAssistedDispatchQueueItem): string {
  const reference = item.bookingId || item.tripId;
  return [
    `Hi ${item.requesterName || 'there'}, this is ShiftWagon support.`,
    `We received your booking${reference ? ` ${reference}` : ''} and are arranging a verified driver for you.`,
    'We will update you as soon as dispatch is confirmed.',
  ].join(' ');
}

function assignReason(item: AdminAssistedDispatchQueueItem, driver: AdminAssistedDispatchCandidate): string {
  return `Admin assigned ${driver.name || driver.driverId} to WhatsApp booking ${item.bookingId}.`;
}

const AdminWhatsAppBookingsPage: React.FC = () => {
  const [queue, setQueue] = useState<AdminAssistedDispatchQueueItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [candidates, setCandidates] = useState<Record<string, CandidateState>>({});
  const [assigningKey, setAssigningKey] = useState('');
  const [messageTarget, setMessageTarget] = useState<AdminAssistedDispatchQueueItem | null>(null);
  const [messageBody, setMessageBody] = useState('');
  const [messageSending, setMessageSending] = useState(false);
  const [messageError, setMessageError] = useState('');

  const queueKey = (item: AdminAssistedDispatchQueueItem) => `${item.tripId}:${item.bookingId}`;

  const loadQueue = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchAdminAssistedDispatchQueue({
        page: 0,
        size: 50,
        bookingSource: 'WHATSAPP_ASSISTED_BOOKING',
        adminDispatchRequired: true,
      });
      setQueue(response.items);
      setTotal(response.totalElements);
    } catch (loadError: any) {
      setError(loadError.message || 'Could not load WhatsApp bookings.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadQueue();
  }, []);

  const stats = useMemo(
    () => ({
      needsDispatch: total,
      withPhone: queue.filter((item) => item.requesterPhone).length,
      scheduledToday: queue.filter((item) => {
        if (!item.scheduledAt) return false;
        return new Date(item.scheduledAt).toDateString() === new Date().toDateString();
      }).length,
    }),
    [queue, total],
  );

  const findDrivers = async (item: AdminAssistedDispatchQueueItem) => {
    const key = queueKey(item);
    setNotice('');
    setCandidates((current) => ({
      ...current,
      [key]: { loading: true, error: '', items: current[key]?.items ?? [] },
    }));
    try {
      const items = await fetchAdminAssistedDispatchCandidates(item.tripId, {
        bookingId: item.bookingId,
        radiusKm: 10,
        limit: 20,
      });
      setCandidates((current) => ({
        ...current,
        [key]: { loading: false, error: '', items },
      }));
    } catch (candidateError: any) {
      setCandidates((current) => ({
        ...current,
        [key]: { loading: false, error: candidateError.message || 'Could not find drivers.', items: [] },
      }));
    }
  };

  const assignDriver = async (item: AdminAssistedDispatchQueueItem, driver: AdminAssistedDispatchCandidate) => {
    const key = `${queueKey(item)}:${driver.driverId}`;
    setAssigningKey(key);
    setNotice('');
    setError('');
    try {
      await assignAdminAssistedDispatchDriver(item.tripId, {
        bookingId: item.bookingId,
        driverId: driver.driverId,
        reason: assignReason(item, driver),
      });
      let customerNotice = '';
      if (item.requesterPhone) {
        try {
          await sendAdminWhatsAppMessage({
            to: item.requesterPhone,
            body: [
              `Good news, ${item.requesterName || 'there'}.`,
              `A verified ShiftWagon driver has been assigned to booking ${item.bookingId}.`,
              'You can keep using your tracking link for live updates.',
            ].join(' '),
          });
          customerNotice = ' Customer was notified on WhatsApp.';
        } catch {
          customerNotice = ' Driver assigned, but the WhatsApp customer update could not be queued.';
        }
      }
      setNotice(`Assigned ${driver.name || driver.driverId} to ${item.bookingId}.${customerNotice}`);
      await loadQueue();
    } catch (assignError: any) {
      setError(assignError.message || 'Could not assign the selected driver.');
    } finally {
      setAssigningKey('');
    }
  };

  const openWhatsAppMessage = (item: AdminAssistedDispatchQueueItem) => {
    setMessageTarget(item);
    setMessageBody(defaultWhatsAppAdminMessage(item));
    setMessageError('');
  };

  const sendWhatsAppMessage = async () => {
    if (!messageTarget?.requesterPhone) {
      setMessageError('This WhatsApp booking has no customer phone number.');
      return;
    }
    if (!messageBody.trim()) {
      setMessageError('Enter a message before sending.');
      return;
    }
    setMessageSending(true);
    setMessageError('');
    try {
      await sendAdminWhatsAppMessage({ to: messageTarget.requesterPhone, body: messageBody.trim() });
      setNotice('WhatsApp message queued for delivery.');
      setMessageTarget(null);
      setMessageBody('');
    } catch (sendError: any) {
      setMessageError(sendError.message || 'Could not send the WhatsApp message.');
    } finally {
      setMessageSending(false);
    }
  };

  return (
    <AdminShell
      badge="WhatsApp"
      copy="Monitor WhatsApp-assisted bookings, arrange drivers when automatic matching needs help, and keep the customer updated from admin."
      title="WhatsApp bookings"
    >
      {error ? <div className="register-inline-error">{error}</div> : null}
      {notice ? <div className="admin-message-alert">{notice}</div> : null}

      <div className="admin-grid admin-grid-cards" style={{ marginBottom: '18px' }}>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Need dispatch</span>
          <strong>{formatCompactNumber(stats.needsDispatch)}</strong>
          <span className="admin-stat-note">Confirmed WhatsApp bookings waiting for admin driver assignment.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Have WhatsApp phone</span>
          <strong>{formatCompactNumber(stats.withPhone)}</strong>
          <span className="admin-stat-note">Rows admin can message directly through the backend WhatsApp channel.</span>
        </div>
        <div className="admin-panel admin-stat-card">
          <span className="admin-stat-label">Scheduled today</span>
          <strong>{formatCompactNumber(stats.scheduledToday)}</strong>
          <span className="admin-stat-note">Jobs where operations should move fastest.</span>
        </div>
      </div>

      <section className="admin-panel">
        <div className="admin-panel-header">
          <div>
            <h2 className="admin-panel-title">Admin dispatch queue</h2>
            <p className="admin-panel-copy">Search near the pickup, assign a driver, or message the customer while dispatch is being arranged.</p>
          </div>
          <button className="register-secondary-button" disabled={loading} onClick={() => void loadQueue()} type="button">
            Refresh
          </button>
        </div>

        {loading ? (
          <div className="register-loader" />
        ) : queue.length ? (
          <div className="admin-whatsapp-dispatch-list">
            {queue.map((item) => {
              const key = queueKey(item);
              const candidateState = candidates[key];
              return (
                <div className="admin-whatsapp-dispatch-card admin-whatsapp-dispatch-card-expanded" key={key}>
                  <div>
                    <div className="admin-whatsapp-dispatch-title">
                      <strong>{item.bookingId || item.tripId}</strong>
                      <span className="admin-pill admin-pill-whatsapp">WhatsApp</span>
                      <span className="admin-pill admin-pill-warning">Admin dispatch required</span>
                    </div>
                    <small className="admin-muted-note">Scheduled: {formatDateTime(item.scheduledAt)}</small>
                    <small className="admin-muted-note">Pickup: {item.sourceAddress || 'Pickup pending'}</small>
                    <small className="admin-muted-note">Drop-off: {item.destinationAddress || 'Drop-off pending'}</small>
                  </div>
                  <div>
                    <span className="admin-stat-label">Customer</span>
                    <strong>{item.requesterName || 'WhatsApp customer'}</strong>
                    <small className="admin-muted-note">{item.requesterPhone || 'No WhatsApp number'}</small>
                    <small className="admin-muted-note">{formatCurrency(item.estimatedFare ?? item.fareAmount)}</small>
                  </div>
                  <div>
                    <span className="admin-stat-label">Vehicle</span>
                    <strong>{item.vehicleType || 'Vehicle pending'}</strong>
                    <small className="admin-muted-note">{formatCompactNumber(item.driverCandidateCount)} original candidates</small>
                  </div>
                  <div className="admin-table-actions">
                    <Link className="admin-row-link" to={`/admin/trips/${item.tripId}`}>
                      View trip
                    </Link>
                    <button className="admin-payment-action-button" onClick={() => void findDrivers(item)} type="button">
                      {candidateState?.loading ? 'Searching...' : 'Find drivers near pickup'}
                    </button>
                    <button
                      className="admin-payment-action-button"
                      disabled={!item.requesterPhone}
                      onClick={() => openWhatsAppMessage(item)}
                      type="button"
                    >
                      Message customer
                    </button>
                  </div>
                  {candidateState ? (
                    <div className="admin-whatsapp-candidates">
                      {candidateState.error ? <div className="register-inline-error">{candidateState.error}</div> : null}
                      {!candidateState.loading && !candidateState.error && !candidateState.items.length ? (
                        <p className="admin-empty">No eligible drivers found within 10 km of pickup.</p>
                      ) : null}
                      {candidateState.items.map((driver) => (
                        <div className="admin-whatsapp-candidate-card" key={driver.driverId}>
                          <div>
                            <strong>{driver.name || 'Driver'}</strong>
                            <small className="admin-muted-note">{driver.driverId}</small>
                          </div>
                          <div>
                            <span className="admin-stat-label">Distance</span>
                            <strong>{driver.distanceKm == null ? 'Unknown' : `${driver.distanceKm.toFixed(1)} km`}</strong>
                            <small className="admin-muted-note">{driver.etaToPickupMin == null ? 'ETA unknown' : `${driver.etaToPickupMin} min to pickup`}</small>
                          </div>
                          <div>
                            <span className="admin-stat-label">Availability</span>
                            <strong>{driver.availableForRequestedSlot === false ? 'Not available' : 'Available'}</strong>
                            <small className="admin-muted-note">{driver.status || driver.vehicleType || 'Status pending'}</small>
                          </div>
                          <button
                            className="register-secondary-button"
                            disabled={assigningKey === `${key}:${driver.driverId}` || driver.availableForRequestedSlot === false}
                            onClick={() => void assignDriver(item, driver)}
                            type="button"
                          >
                            {assigningKey === `${key}:${driver.driverId}` ? 'Assigning...' : 'Assign driver'}
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="admin-empty">No WhatsApp bookings currently require admin dispatch.</p>
        )}
      </section>

      {messageTarget ? (
        <div className="admin-modal-backdrop" role="presentation">
          <div aria-modal="true" className="admin-modal" role="dialog">
            <div className="admin-modal-header">
              <div>
                <h2>Message customer on WhatsApp</h2>
                <p>{messageTarget.requesterPhone || 'No phone number available'}</p>
              </div>
              <button
                aria-label="Close WhatsApp message dialog"
                className="admin-modal-close"
                disabled={messageSending}
                onClick={() => setMessageTarget(null)}
                type="button"
              >
                x
              </button>
            </div>
            {messageError ? <div className="register-inline-error">{messageError}</div> : null}
            <label className="admin-modal-field">
              <span>Message</span>
              <textarea
                className="register-input"
                maxLength={4096}
                onChange={(event) => setMessageBody(event.target.value)}
                value={messageBody}
              />
            </label>
            <div className="admin-modal-actions">
              <button className="register-secondary-button" disabled={messageSending} onClick={sendWhatsAppMessage} type="button">
                {messageSending ? 'Sending...' : 'Send WhatsApp message'}
              </button>
              <button
                className="admin-payment-action-button"
                disabled={messageSending}
                onClick={() => setMessageTarget(null)}
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </AdminShell>
  );
};

export default AdminWhatsAppBookingsPage;
